import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Upload, PawPrint, Ban, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileImage } from "@/components/FileImage";
import { BreedSelect } from "@/components/BreedSelect";
import { api, API } from "@/lib/api";
import { addMonths } from "@/lib/format";

const EMPTY = {
  name: "", breed: "", gender: "", neutered_spayed: false, weight_kg: "",
  date_of_birth: "", color: "", microchip_id: "", diet_notes: "", food_allergies: "",
  medications: "", medical_conditions: "",
  temperament: [], social_notes: "",
  vaccination_last_date: "", vaccination_next_due: "", kennel_cough_date: "",
  tick_treatment_last: "", tick_treatment_next: "", deworming_last: "", deworming_next: "",
};

const MONTH_KEYS = [
  "vaccination_last_date", "vaccination_next_due", "kennel_cough_date",
  "tick_treatment_last", "tick_treatment_next", "deworming_last", "deworming_next",
];

// Industry-standard intervals: vaccination yearly, tick & flea monthly, deworming quarterly
const AUTO_NEXT = {
  vaccination_last_date: ["vaccination_next_due", 12],
  tick_treatment_last: ["tick_treatment_next", 1],
  deworming_last: ["deworming_next", 3],
};

const KIND_LABELS = { photo: "Photo", vaccination: "Vaccination", injury: "Injury", medical: "Medical", other: "Other" };

const TEMPERAMENTS = [
  "Friendly with all", "Shy / needs slow intros", "High energy",
  "Prefers humans over dogs", "Senior / calm", "Anxious", "Playful", "Protective",
];
const tSlug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <input
      {...props}
      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
    />
  </div>
);

const Area = ({ label, ...props }) => (
  <div className="sm:col-span-2">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <textarea
      {...props}
      rows={2}
      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
    />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <select
      {...props}
      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
    >
      {children}
    </select>
  </div>
);

export const PupModal = ({ open, onClose, pup, onSaved }) => {
  const [createdPup, setCreatedPup] = useState(null);
  const activePup = pup || createdPup;
  const isEdit = Boolean(activePup?.pup_id);
  const req = !isEdit;
  const navigate = useNavigate();
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(EMPTY);
  const [docs, setDocs] = useState([]);
  const [photoPath, setPhotoPath] = useState("");
  const [kind, setKind] = useState("vaccination");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) {
      setTab("details");
      setConfirmDelete(false);
      setCreatedPup(null);
      if (pup) {
        const f = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => {
          if (pup[k] !== null && pup[k] !== undefined) f[k] = pup[k];
        });
        MONTH_KEYS.forEach((k) => {
          if (typeof f[k] === "string") f[k] = f[k].slice(0, 7);
        });
        f.temperament = Array.isArray(f.temperament) ? f.temperament : (f.temperament ? [f.temperament] : []);
        setForm(f);
        setDocs(pup.documents || []);
        setPhotoPath(pup.photo_path || "");
      } else {
        setForm(EMPTY);
        setDocs([]);
        setPhotoPath("");
      }
    }
  }, [open, pup]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      const rule = AUTO_NEXT[k];
      if (rule && v && !f[rule[0]]) next[rule[0]] = addMonths(v, rule[1]);
      return next;
    });
  };

  const toggleTemperament = (t) =>
    setForm((f) => {
      const cur = Array.isArray(f.temperament) ? f.temperament : [];
      return { ...f, temperament: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] };
    });

  const REQUIRED_NEW = [
    ["name", "name", (f) => !String(f.name).trim()],
    ["breed", "breed", (f) => !String(f.breed).trim()],
    ["weight", "weight (kg)", (f) => f.weight_kg === "" || f.weight_kg === null || f.weight_kg === undefined],
    ["gender", "gender", (f) => !f.gender],
    ["date_of_birth", "date of birth", (f) => !f.date_of_birth],
    ["temperament", "temperament (pick at least one)", (f) => !(f.temperament || []).length],
    ["vaccination_last_date", "vaccination last-done month", (f) => !f.vaccination_last_date],
    ["vaccination_next_due", "vaccination next-due month", (f) => !f.vaccination_next_due],
  ];

  const save = async (e) => {
    e.preventDefault();
    if (!isEdit) {
      const missing = REQUIRED_NEW.filter(([, , test]) => test(form)).map(([, label]) => label);
      if (missing.length) {
        toast.error(`Please add: ${missing.join(", ")}`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`/pups/${activePup.pup_id}`, form)
        : await api.post("/pups", form);
      if (isEdit) {
        toast.success("Profile updated");
        onSaved(res.data);
      } else {
        toast.success(`${res.data.name} has joined the pack! Now add their vaccination chart.`);
        onSaved(res.data);
        setCreatedPup(res.data);
        setDocs(res.data.documents || []);
        setPhotoPath(res.data.photo_path || "");
        setKind("vaccination");
        setTab("documents");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await api.post(`/pups/${activePup.pup_id}/documents`, fd);
      setDocs((d) => [...d, res.data]);
      if (kind === "photo") setPhotoPath(res.data.storage_path);
      toast.success(`${KIND_LABELS[kind]} uploaded`);
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "photo");
      const res = await api.post(`/pups/${activePup.pup_id}/documents`, fd);
      setDocs((d) => [...d, res.data]);
      setPhotoPath(res.data.storage_path);
      toast.success("Profile photo updated");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/pups/${activePup.pup_id}/documents/${docId}`);
      setDocs((d) => d.filter((x) => x.doc_id !== docId));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove file");
    }
  };

  const toggleArchive = async () => {
    setSaving(true);
    try {
      const next = !activePup.archived;
      await api.put(`/pups/${activePup.pup_id}`, { archived: next });
      toast.success(next ? `${activePup.name}'s profile disabled — reminders stopped` : `${activePup.name}'s profile restored`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const deletePup = async () => {
    setSaving(true);
    try {
      await api.delete(`/pups/${activePup.pup_id}`);
      toast.success(`${activePup.name}'s profile deleted`);
      setConfirmDelete(false);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pup-modal" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-ink">
            {isEdit ? `${activePup.name}'s profile` : "Add a new pup"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isEdit ? "Update details, health dates and documents." : "Tell us the basics — you can add photos and records after saving."}
          </DialogDescription>
        </DialogHeader>

        {isEdit && (
          <div className="mb-4 grid grid-cols-2 rounded-full bg-cream-dark p-1 text-sm font-semibold">
            {["details", "documents"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`pup-tab-${t}`}
                onClick={() => setTab(t)}
                className={`rounded-full py-2 capitalize transition-colors duration-200 ${
                  tab === t ? "bg-navy text-cream" : "text-navy/60"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {tab === "details" && (
          <>
          <form data-testid="pup-form" onSubmit={save} noValidate className="grid gap-4 sm:grid-cols-2">
            {isEdit && (
              <div className="flex items-center gap-4 sm:col-span-2">
                {photoPath ? (
                  <FileImage path={photoPath} alt={activePup.name} className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-bark-soft" />
                ) : (
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-bark-soft text-bark">
                    <PawPrint className="h-7 w-7" aria-hidden="true" />
                  </span>
                )}
                <div>
                  <label
                    data-testid="pup-photo-upload-label"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                  >
                    <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                    {uploading ? "Uploading…" : photoPath ? "Change photo" : "Add a photo"}
                    <input data-testid="pup-photo-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} />
                  </label>
                  <p className="mt-1 text-xs text-slate-400">This becomes {activePup.name}'s picture on your dashboard.</p>
                </div>
              </div>
            )}
            <Input label="Name *" data-testid="pup-name" value={form.name} onChange={set("name")} placeholder="Bruno" />
            <div>
              <BreedSelect
                key={activePup?.pup_id || "new"}
                testid="pup-breed"
                value={form.breed}
                onChange={(v) => setForm((f) => ({ ...f, breed: v }))}
              />
              {req && <p className="mt-1 text-[11px] font-semibold text-bark">Required</p>}
            </div>
            <Select label={`Gender${req ? " *" : ""}`} data-testid="pup-gender" value={form.gender} onChange={set("gender")}>
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Select>
            <Input label={`Date of birth${req ? " *" : ""}`} data-testid="pup-dob" type="date" value={form.date_of_birth || ""} onChange={set("date_of_birth")} />
            <Input label={`Weight (kg)${req ? " *" : ""}`} data-testid="pup-weight" type="number" step="0.1" min="0" value={form.weight_kg} onChange={set("weight_kg")} placeholder="18" />
            <Input label="Colour" data-testid="pup-color" value={form.color} onChange={set("color")} placeholder="Golden" />
            <Input label="Microchip ID" data-testid="pup-microchip" value={form.microchip_id} onChange={set("microchip_id")} placeholder="Optional" />
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Temperament{req ? " *" : ""}</label>
              <p className="mt-0.5 text-xs text-slate-400">Pick all that apply — more than one is fine.</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="pup-temperament">
                {TEMPERAMENTS.map((t) => {
                  const on = (form.temperament || []).includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`pup-temperament-${tSlug(t)}`}
                      aria-pressed={on}
                      onClick={() => toggleTemperament(t)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${on ? "border-bark bg-bark-soft text-bark" : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <Area label="Social notes / anything else" data-testid="pup-social" value={form.social_notes} onChange={set("social_notes")} placeholder="Loves fetch, scared of loud noises…" />
            <label className="flex items-center gap-3 rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm font-semibold text-navy sm:col-span-2">
              <input
                type="checkbox"
                data-testid="pup-neutered"
                checked={!!form.neutered_spayed}
                onChange={set("neutered_spayed")}
                className="h-4 w-4 accent-[#EA580C]"
              />
              Neutered / spayed
            </label>
            <Area label="Diet & feeding notes" data-testid="pup-diet" value={form.diet_notes} onChange={set("diet_notes")} placeholder="Portion size, preferences, feeding schedule…" />
            <Area label="Food allergies" data-testid="pup-allergies" value={form.food_allergies} onChange={set("food_allergies")} placeholder="Chicken, grains, none…" />
            <Area label="Current medications" data-testid="pup-medications" value={form.medications} onChange={set("medications")} placeholder="Name, dose, timing…" />
            <Area label="Medical conditions / past injuries" data-testid="pup-medical" value={form.medical_conditions} onChange={set("medical_conditions")} placeholder="Anything our team and vet should know…" />
            <div className="sm:col-span-2 mt-1 border-t border-ink/8 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Health &amp; medication months</p>
              <p className="mt-1 text-xs text-slate-400">
                Month &amp; year is enough. Set a "last done" month and we'll pre-fill the next due —
                vaccination yearly, tick &amp; flea monthly, deworming every 3 months. Adjust anytime.
              </p>
            </div>
            <Input label={`Vaccination (last)${req ? " *" : ""}`} data-testid="pup-vax-last" type="month" value={form.vaccination_last_date} onChange={set("vaccination_last_date")} />
            <Input label={`Vaccination next due${req ? " *" : ""}`} data-testid="pup-vax-due" type="month" value={form.vaccination_next_due} onChange={set("vaccination_next_due")} />
            <Input label="Kennel cough vaccine" data-testid="pup-kennel" type="month" value={form.kennel_cough_date} onChange={set("kennel_cough_date")} />
            <Input label="Tick & flea (last)" data-testid="pup-tick-last" type="month" value={form.tick_treatment_last} onChange={set("tick_treatment_last")} />
            <Input label="Tick & flea next due" data-testid="pup-tick-due" type="month" value={form.tick_treatment_next} onChange={set("tick_treatment_next")} />
            <Input label="Deworming (last)" data-testid="pup-deworm-last" type="month" value={form.deworming_last} onChange={set("deworming_last")} />
            <Input label="Deworming next due" data-testid="pup-deworm-due" type="month" value={form.deworming_next} onChange={set("deworming_next")} />
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="pup-save"
                disabled={saving}
                className="w-full rounded-full bg-bark px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
              >
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create pup profile"}
              </button>
              {!isEdit && (
                <p className="mt-3 text-center text-xs text-slate-400">
                  Fields marked * are required. You'll add the vaccination chart on the next step.
                </p>
              )}
            </div>
          </form>
          {isEdit && (
            <div data-testid="pup-danger-zone" className="mt-5 rounded-2xl border border-red-200 bg-red-50/50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-500">Manage profile</p>
              <p className="mt-1 text-xs text-slate-500">
                {activePup?.archived
                  ? "This profile is disabled — it's hidden from booking and won't get health reminders."
                  : "Disable to stop reminders and hide from booking (restore anytime), or delete permanently."}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="pup-archive-toggle"
                  onClick={toggleArchive}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark disabled:opacity-60"
                >
                  {activePup?.archived ? <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> : <Ban className="h-3.5 w-3.5" aria-hidden="true" />}
                  {activePup?.archived ? "Restore profile" : "Disable profile"}
                </button>
                {confirmDelete ? (
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="pup-delete-confirm"
                      onClick={deletePup}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Confirm delete
                    </button>
                    <button
                      type="button"
                      data-testid="pup-delete-cancel"
                      onClick={() => setConfirmDelete(false)}
                      className="rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-navy"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="pup-delete-button"
                    onClick={() => setConfirmDelete(true)}
                    className="inline-flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition-colors duration-200 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Delete permanently
                  </button>
                )}
              </div>
            </div>
          )}
          </>
        )}

        {tab === "documents" && isEdit && (
          <div data-testid="pup-documents">
            {createdPup && (
              <p className="mb-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                {createdPup.name}'s profile is saved. Upload the vaccination chart below to finish.
              </p>
            )}
            <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-ink/15 bg-cream p-4 sm:flex-row sm:flex-wrap sm:items-center">
              <select
                data-testid="doc-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="w-full rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold text-navy focus:outline-none sm:w-auto"
              >
                {Object.entries(KIND_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              <label
                data-testid="doc-upload-label"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink sm:w-auto"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                {uploading ? "Uploading…" : "Upload file"}
                <input data-testid="doc-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={upload} disabled={uploading} />
              </label>
              <p className="w-full text-xs leading-relaxed text-slate-400 sm:flex-1">Images or PDF, up to 10 MB. Kind "Photo" becomes the profile picture.</p>
            </div>
            <p className="mt-3 rounded-2xl bg-amberwarm/10 px-4 py-2.5 text-xs text-slate-600">
              A vaccination chart is required before a pup can be booked — upload it here with kind “Vaccination”.
            </p>

            {docs.length === 0 ? (
              <div className="mt-6 flex flex-col items-center gap-2 rounded-2xl bg-cream-dark/60 py-10 text-center">
                <PawPrint className="h-8 w-8 text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No files yet — vaccination cards, injury photos and more live here.</p>
              </div>
            ) : (
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {docs.map((d) => (
                  <li key={d.doc_id} data-testid={`doc-${d.doc_id}`} className="flex items-center gap-3 rounded-2xl border border-ink/8 bg-white p-3">
                    <a
                      href={`${API}/files/${d.storage_path}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`doc-view-${d.doc_id}`}
                      className="group flex min-w-0 flex-1 items-center gap-3"
                    >
                      {d.content_type?.startsWith("image/") ? (
                        <FileImage path={d.storage_path} alt={d.original_filename} className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                      ) : (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-cream-dark text-[10px] font-bold text-slate-500">PDF</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink group-hover:text-bark">{d.original_filename}</p>
                        <p className="text-xs uppercase tracking-wider text-bark">{KIND_LABELS[d.kind]} <span className="normal-case tracking-normal text-slate-400">· tap to view</span></p>
                      </div>
                    </a>
                    <button
                      type="button"
                      data-testid={`doc-delete-${d.doc_id}`}
                      onClick={() => removeDoc(d.doc_id)}
                      aria-label="Delete file"
                      className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {createdPup && (
              <button
                type="button"
                data-testid="pup-done-continue"
                disabled={!docs.some((d) => d.kind === "vaccination")}
                onClick={() => { onClose(); navigate("/account/book"); }}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-50"
              >
                Done — continue to booking
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
